"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import {
  ApiError,
  startMagicCode,
  verifyMagicCode,
  verifyGoogleSignIn,
  type VerifyResponse,
} from "../../lib/api";
import {
  accountFromPayload,
  readToken,
  readClientId,
  writeAuth,
} from "../../lib/auth";
import { track } from "../../lib/track";
import { GoogleSignInButton } from "./google-button";

type Step = "email" | "code" | "success";

const RESEND_COOLDOWN = 30;
const CODE_LENGTH = 6;
// Inlined at build time — controls whether the "Continue with Google" block
// (button + divider) renders. Keeps the email-only flow clean when Google
// sign-in isn't configured for the environment.
const GOOGLE_ENABLED = !!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

/**
 * Smart back link — surfaces "Back to pricing" / "Back to Blog" etc.
 * when the user came from a specific marketing page, and `null` when
 * the referrer is unknown / external / the homepage. We hide the
 * link entirely in the `null` case because the brand lockup at the
 * top of the page is already a Link to `/` — showing both would be
 * redundant chrome on what should be a focused task surface.
 */
function useSmartBackLink(): { href: string; label: string } | null {
  const [link, setLink] = useState<{ href: string; label: string } | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const ref = document.referrer;
    if (!ref) return;
    try {
      const url = new URL(ref);
      if (url.origin !== window.location.origin) return;
      const path = url.pathname;
      if (path === "/login" || path === "/") return;
      const label =
        path === "/pricing" ? "Back to pricing"
        : path === "/about" ? "Back to About"
        : path === "/blogs" ? "Back to Blog"
        : path.startsWith("/blogs/") ? "Back to Blog"
        : path === "/contact" ? "Back to Contact"
        : path === "/updates" ? "Back to Roadmap"
        : null;
      if (label) setLink({ href: path, label });
    } catch {
      /* ignore — keep the default null */
    }
  }, []);

  return link;
}

interface LoginClientProps {
  /**
   * When set, the component is being rendered inline (e.g. inside the
   * guest-audit "sign in to unlock" modal) rather than as the /login page.
   * In embedded mode it does NOT auto-redirect an already-signed-in visitor,
   * and on a successful verify it calls `onSuccess(data)` instead of routing
   * to `next` — the host decides what happens (e.g. claim the audit + redirect).
   */
  embedded?: boolean;
  onSuccess?: (data: VerifyResponse) => void;
}

export function LoginClient({ embedded = false, onSuccess }: LoginClientProps = {}) {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const back = useSmartBackLink();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [codeDigits, setCodeDigits] = useState<string[]>(
    () => new Array(CODE_LENGTH).fill(""),
  );
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    // Embedded (modal) mode never bounces an already-signed-in visitor — the
    // host handles that case (it auto-claims and redirects itself).
    if (embedded) return;
    if (readToken()) router.replace(next);
  }, [router, next, embedded]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(
      () => setResendCooldown((s) => Math.max(0, s - 1)),
      1000
    );
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  const codeString = useMemo(() => codeDigits.join(""), [codeDigits]);

  async function handleEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned) {
      setError("Enter your email to receive a sign-in code.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await startMagicCode(cleaned, readClientId());
      setPendingEmail(cleaned);
      setStep("code");
      setStatus(`We emailed a 6-digit code to ${cleaned}.`);
      setResendCooldown(RESEND_COOLDOWN);
      setCodeDigits(new Array(CODE_LENGTH).fill(""));
    } catch (err) {
      setError(messageOf(err, "Could not send code."));
    } finally {
      setBusy(false);
    }
  }

  async function handleCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (codeString.length !== CODE_LENGTH) {
      setError("Enter all 6 digits of the code we emailed.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await verifyMagicCode({
        email: pendingEmail,
        code: codeString,
        clientId: readClientId(),
      });
      writeAuth(data.token, accountFromPayload(data));
      // Funnel end-step. context separates the /try unlock modal
      // (embedded) from the standalone /login page.
      track("Sign In Completed", {
        method: "code",
        context: embedded ? "embedded" : "page",
      });
      markWelcomePending();
      if (onSuccess) {
        onSuccess(data);
        return;
      }
      setStep("success");
      await new Promise((r) => window.setTimeout(r, 500));
      router.replace(next);
    } catch (err) {
      setError(messageOf(err, "That code did not work."));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogleCredential(credential: string) {
    setBusy(true);
    setError("");
    try {
      const data = await verifyGoogleSignIn({
        credential,
        clientId: readClientId(),
      });
      writeAuth(data.token, accountFromPayload(data));
      track("Sign In Completed", {
        method: "google",
        context: embedded ? "embedded" : "page",
      });
      markWelcomePending();
      if (onSuccess) {
        onSuccess(data);
        return;
      }
      setStep("success");
      await new Promise((r) => window.setTimeout(r, 500));
      router.replace(next);
    } catch (err) {
      setError(messageOf(err, "Google sign-in didn't work."));
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || resending || !pendingEmail) return;
    setResending(true);
    setError("");
    try {
      await startMagicCode(pendingEmail, readClientId());
      setStatus(`We sent a new code to ${pendingEmail}.`);
      setResendCooldown(RESEND_COOLDOWN);
      setCodeDigits(new Array(CODE_LENGTH).fill(""));
    } catch (err) {
      setError(messageOf(err, "Could not resend code."));
    } finally {
      setResending(false);
    }
  }

  function backToEmail() {
    setStep("email");
    setCodeDigits(new Array(CODE_LENGTH).fill(""));
    setPendingEmail("");
    setError("");
    setStatus("");
  }

  return (
    <div className="login-shell">
      <div className="login-card-shell">
        {back ? (
          <Link href={back.href} className="login-back">
            ← {back.label}
          </Link>
        ) : null}

        <div className="login-card">
          {step === "success" ? (
            <div className="login-success" aria-live="polite">
              <span className="login-success-mark" aria-hidden="true">✓</span>
              <span>
                <strong>Signed in.</strong>
                <span className="login-success-sub">
                  Taking you to your dashboard…
                </span>
              </span>
            </div>
          ) : step === "code" ? (
            <>
              <header className="login-card-header">
                <h1>Check your inbox</h1>
                <p>
                  We sent a 6-digit code to <strong>{pendingEmail}</strong>.
                </p>
              </header>

              {error ? (
                <div className="login-alert" role="alert">{error}</div>
              ) : null}

              <form onSubmit={handleCode} noValidate>
                <fieldset className="login-otp-fieldset">
                  <legend className="login-otp-legend">6-digit code</legend>
                  <OtpInput
                    value={codeDigits}
                    onChange={setCodeDigits}
                    disabled={busy}
                  />
                </fieldset>
                <button
                  type="submit"
                  className="login-primary"
                  disabled={busy || codeString.length !== CODE_LENGTH}
                >
                  {busy ? "Verifying…" : "Verify and continue"}
                </button>
                <div className="login-secondary-row">
                  <button
                    type="button"
                    className="login-link-button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resending}
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : resending
                        ? "Resending…"
                        : "Resend code"}
                  </button>
                  <button
                    type="button"
                    className="login-link-button"
                    onClick={backToEmail}
                  >
                    Use a different email
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <header className="login-card-header">
                <h1>Sign in to Snap Site</h1>
                <p>Continue with Google, or we&rsquo;ll email you a 6-digit code.</p>
              </header>

              {error ? (
                <div className="login-alert" role="alert">{error}</div>
              ) : null}

              {/* Google sign-in renders only when configured; the divider
                  and email form below are the always-available fallback. */}
              {GOOGLE_ENABLED ? (
                <>
                  <GoogleSignInButton onCredential={handleGoogleCredential} />
                  <div className="login-divider" aria-hidden="true">
                    <span>or</span>
                  </div>
                </>
              ) : null}

              <form onSubmit={handleEmail} noValidate>
                <label className="login-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@studio.com"
                    autoComplete="email"
                    inputMode="email"
                    required
                  />
                </label>
                <button
                  type="submit"
                  className="login-primary"
                  disabled={busy}
                >
                  {busy ? "Sending code…" : "Email me a code"}
                </button>
                <p className="login-fineprint">
                  {/* aria-labels spell out the brand + document type
                      so screen-reader users hear "Snap Site Terms
                      of Service" instead of a bare "Terms" link
                      that gives no context about which company's
                      terms they'd be reading. */}
                  <Link href="/terms" aria-label="Snap Site Terms of Service">
                    Terms
                  </Link>
                  {" · "}
                  <Link href="/privacy" aria-label="Snap Site Privacy Policy">
                    Privacy
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>

        {step === "email" ? (
          <p className="login-newuser-note">
            New to Snap Site? An account is created on first sign-in — 5
            free credits included.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Six segmented inputs — one digit each, auto-advance on input, with
 * backspace, arrow-key, and paste-from-email handling. Visually clearer
 * than a single 6-char text field; matches the OTP pattern banks +
 * Apple use.
 */
function OtpInput({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  disabled: boolean;
}) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (value.every((d) => d === "")) {
      inputsRef.current[0]?.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setRef = useCallback(
    (i: number) => (el: HTMLInputElement | null) => {
      inputsRef.current[i] = el;
    },
    [],
  );

  function handleDigitChange(i: number) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, "");
      if (raw.length > 1) {
        const next = [...value];
        for (let j = 0; j < raw.length && i + j < CODE_LENGTH; j++) {
          next[i + j] = raw[j];
        }
        onChange(next);
        const focusAt = Math.min(i + raw.length, CODE_LENGTH - 1);
        inputsRef.current[focusAt]?.focus();
        return;
      }
      const next = [...value];
      next[i] = raw;
      onChange(next);
      if (raw && i < CODE_LENGTH - 1) {
        inputsRef.current[i + 1]?.focus();
      }
    };
  }

  function handleKeyDown(i: number) {
    return (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !value[i] && i > 0) {
        e.preventDefault();
        const next = [...value];
        next[i - 1] = "";
        onChange(next);
        inputsRef.current[i - 1]?.focus();
        return;
      }
      if (e.key === "ArrowLeft" && i > 0) {
        e.preventDefault();
        inputsRef.current[i - 1]?.focus();
        return;
      }
      if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
        e.preventDefault();
        inputsRef.current[i + 1]?.focus();
        return;
      }
    };
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const next = new Array(CODE_LENGTH).fill("");
    for (let i = 0; i < CODE_LENGTH && i < text.length; i++) {
      next[i] = text[i];
    }
    onChange(next);
    const focusAt = Math.min(text.length, CODE_LENGTH - 1);
    inputsRef.current[focusAt]?.focus();
  }

  return (
    <div className="login-otp" role="group" aria-label="6-digit code">
      {value.map((digit, i) => (
        <input
          key={i}
          ref={setRef(i)}
          className="login-otp-box"
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
          value={digit}
          onChange={handleDigitChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Flag a "welcome banner is owed" for the dashboard to render on the
 * next page load. We only mark it on first sign-in for this device —
 * if the user has already seen and dismissed the welcome before, we
 * leave the completed flag alone and don't pester them again.
 *
 * Why localStorage instead of a backend signal: the magic-code flow
 * doesn't currently distinguish "new account created" vs "existing
 * account signed in" in the response payload, and the welcome content
 * is genuinely useful as a "welcome back, here's a refresher" first
 * time on a new device too. A returning user on a familiar device
 * already has the completed flag, so they see nothing.
 */
function markWelcomePending(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem("snap-site:welcome-completed")) return;
    window.localStorage.setItem("snap-site:welcome-pending", "1");
  } catch {
    /* localStorage blocked — banner just won't appear, no harm done. */
  }
}
